/// <amd-module name="Pipeline/Analytics/DurationMetricsTrendCard/ControllerViews/DurationMetricsTrendCard" />

import { ErrorComponent } from "Analytics/Shared/Analytics-Visuals-Common/ErrorComponent/ErrorComponent";
import { LoadingComponent } from "Analytics/Shared/Analytics-Visuals-Common/LoadingComponent/LoadingComponent";
import { TrendType } from "Analytics/Shared/Common/Types";
import { ITrendChartData } from "Analytics/Shared/TrendChart/Common/Types";
import * as Utils_Number from "Analytics/Shared/Utils/Number";
import { getPercentage } from "Analytics/Shared/Utils/Number";
import { AreaChartHelper } from "Analytics/Shared/Visuals/AreaChartView/Common/AreaChartHelper";
import { AreaChartView } from "Analytics/Shared/Visuals/AreaChartView/ControllerViews/AreaChartView";
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import { percentile_text, SuccessfulPipelines } from "Pipeline/Analytics/Common/Constants";
import * as DurationUtils from "Pipeline/Analytics/DurationMetricsTrendCard/DurationUtils";
import * as Resources from "Pipeline/Analytics/DurationMetricsTrendCard/Resources";
import {
    DurationMetricsTrendCardStore,
    IDurationMetricsTrendCardState
} from "Pipeline/Analytics/DurationMetricsTrendCard/Stores/DurationMetricsTrendCardStore";
import { PipelineDurationChartSeries } from "Pipeline/Analytics/PipelineDurationCommon/ChartSeries/PipelineDurationChartSeries";
import { PipelineDurationInsights } from "Pipeline/Analytics/PipelineDurationCommon/PipelineDurationInsights";
import { IPipelineDurationData } from "Pipeline/Analytics/PipelineDurationCommon/Types";
import * as React from "react";
import { filterXSS } from "xss";
import { xssStyleDefaultOptions } from "xss/PresetOptions";
import { localeFormat } from "VSS/Core/Util/String";
import { ChartHostOptions, ChartProps, LabelFormatModes } from "VSS/Features/Charts/Contracts";
import { DataPoint, TooltipLineItem } from "VSS/Features/Charts/Contracts";
import * as Charts_Contracts from "VSS/Features/Charts/Contracts";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Duration } from "VSSUI/Components/Time/Duration/Duration";
import { Icon } from "VSSUI/Icon";
import { SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";

export interface IDurationMetricsTrendCardProps extends IVssComponentProperties {
    store: DurationMetricsTrendCardStore;
    onDataRendered?: () => void;
    errorText?: string;
    updatePipelineDurationData?: (data: IPipelineDurationData | undefined) => void;
}

export class DurationMetricsTrendCard extends VssComponent<IDurationMetricsTrendCardProps, IDurationMetricsTrendCardState> {
    private _onDataRenderedCalled = false;
    private _pipelineDurationInsights: PipelineDurationInsights;
    private _onUpdatePipelineDataCalled = false;

    constructor(props: IDurationMetricsTrendCardProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText
        } as Readonly<IDurationMetricsTrendCardState>;
        this._pipelineDurationInsights = new PipelineDurationInsights();
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
            this.performOnDataRendered();
        }
    }

    public componentDidUpdate(prevProps: IDurationMetricsTrendCardProps, prevState: IDurationMetricsTrendCardState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.performOnDataRendered();
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
        super.componentWillUnmount();
    }

    public render() {
        return (
            <Card
                className={this.props.className}
                titleProps={{
                    text: Resources.CardTitle,
                    ariaLevel: 3
                }}
                headerDescriptionProps={{
                    text: localeFormat(Resources.CardSubTitle, percentile_text)
                }}
            >
                {this.getElementToDisplay()}
            </Card>
        );
    }

    private getElementToDisplay(): JSX.Element {
        if (this.state.errorText) {
            return <ErrorComponent className="duration-metrics-trend-container flex-grow" errorMessage={this.state.errorText} />;
        } else if (this.state.confValues != null && this.state.data != null && this.state.pipelineDurationData != null) {
            const chartData = this.state.data;
            return (
                <div className="duration-metrics-trend-container flex-row flex-wrap flex-grow">
                    {this.state.pipelineDurationData.pipelineCount > 0 && (
                        <>
                            {this.getMetricsDataElement()}
                            <div className="pipeline-duration-trend flex-grow">
                                {this._verifyChartData(chartData) && <AreaChartView chartOptions={this._getChartOptions(chartData)} />}
                            </div>
                        </>
                    )}
                    {this.state.pipelineDurationData.pipelineCount == 0 && (
                        <AnalyticsUnavailableMessage suggestion={Resources.NoSuccessfulPipelineText} iconName="Rocket" />
                    )}
                </div>
            );
        } else {
            return <LoadingComponent className="card-spinner flex-column flex-grow" size={SpinnerSize.large} />;
        }
    }

    private getMetricsDataElement(): JSX.Element {
        const pipelineData = this.state.pipelineDurationData;
        const durationTrend = this.state.data ? DurationUtils.getDurationTrend(this.state.data!) : undefined;
        return (
            <div className="duration-trend-summary flex-column flex-grow">
                <div className="flex-column flex-grow">
                    <div className="duration-metrics-element flex-row">
                        {pipelineData.percentileDuration && (
                            <div className="font-size-xxl flex-self-end">{this.getDurationElement(pipelineData.percentileDuration * 1000)}</div>
                        )}
                        {durationTrend && (
                            <div
                                className={css(
                                    "duration-trend flex-self-end font-weight-semibold",
                                    durationTrend.trendType === TrendType.Increased ? "negative-color" : "positive-color"
                                )}
                            >
                                <Icon
                                    className="trend-icon font-size-xs"
                                    iconName={durationTrend.trendType === TrendType.Increased ? "TriangleSolidUp12" : "TriangleSolidDown12"}
                                />
                                <span className="font-size-m">
                                    {localeFormat(
                                        Resources.TrendFormat,
                                        Utils_Number.formatAbbreviatedNumber(durationTrend.percentageVariation || 0)
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="margin-bottom-4 font-weight-light font-size-m">
                        {localeFormat(Resources.SuccessfulPipelinesCountText, pipelineData.pipelineCount.toLocaleString())}
                    </div>
                </div>

                {this.getDurationInsightsElement()}
            </div>
        );
    }

    private getDurationInsightsElement(): JSX.Element | undefined {
        if (this.state.insightsData) {
            const insightsData = this.state.insightsData;
            const maxDurationTaskPercentage = getPercentage(insightsData.allTasksDuration, insightsData.taskDuration);
            return (
                <div className="duration-metrics-insight flex-row font-size-m font-weight-normal secondary-text">
                    <div className="flex-self-start">
                        <Icon className="duration-metric-insight-icon icon-margin font-size-ml" iconName="CRMCustomerInsightsApp" />
                    </div>
                    <span
                        className="duration-metrics-insight-text"
                        dangerouslySetInnerHTML={{
                            __html: filterXSS(
                                this._pipelineDurationInsights.getPipelineDurationInsights(maxDurationTaskPercentage, insightsData.taskName),
                                xssStyleDefaultOptions
                            )
                        }}
                    />
                </div>
            );
        }
    }

    private _getChartOptions = (chartData: ITrendChartData): ChartProps => {
        const _areaChartHelper: AreaChartHelper = new AreaChartHelper();
        const normalizedData = DurationUtils.normalizeDurationData(chartData);
        const data = DurationUtils.getNormalizedChartData(normalizedData, chartData.primaryChartData[SuccessfulPipelines]);
        const chartSeries = new PipelineDurationChartSeries(data.map(d => d.aggrValue));
        let chartProps = _areaChartHelper.getAreaChartOptions(this.context, data, chartSeries, false, this.state.confValues);
        chartProps = _areaChartHelper.setAreaChartTitle(chartProps, Resources.PipelineDurationTrendAriaLabelText)
        chartProps = _areaChartHelper.setHostOptions(chartProps, { height: 200 } as ChartHostOptions);
        chartProps = _areaChartHelper.setSecondaryAxisLabelFormatMode(
            chartProps,
            LabelFormatModes.Textual,
            chartSeries,
            localeFormat(Resources.YAxisLabel.toLocaleUpperCase(), normalizedData.durationUnitString.toLocaleUpperCase())
        );
        chartProps = _areaChartHelper.setCustomTooltipMapping(chartProps, (points: DataPoint[]): TooltipLineItem[] => {
            return points.map(point => {
                const item = {
                    styleType: {
                        color: point.color,
                        type: Charts_Contracts.TooltipLineItemMarkerType.Square
                    },
                    text: ""
                } as Charts_Contracts.TooltipLineItem;
                const seriesValue = Number(point.values[0]).toLocaleString(navigator.language);

                item.text = `${seriesValue} ` + normalizedData.durationUnitString;
                return item;
            });
        });
        return chartProps;
    };

    private _verifyChartData = (chartData: ITrendChartData | undefined): boolean => {
        return chartData && chartData.primaryChartData[SuccessfulPipelines].length > 0 ? true : false;
    };

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState());
    };

    private performOnDataRendered() {
        if (this.state.data || this.state.errorText) {
            if (this.props.onDataRendered && !this._onDataRenderedCalled) {
                this.props.onDataRendered();
                this._onDataRenderedCalled = true;
            }
        }
        if (this.props.updatePipelineDurationData && !this._onUpdatePipelineDataCalled && this.state.pipelineDurationData) {
            this.props.updatePipelineDurationData(this.state.pipelineDurationData);
            this._onUpdatePipelineDataCalled = true;
        }
    }

    private getDurationElement = (value: number): JSX.Element => {
        return <Duration startDate={new Date(0)} endDate={new Date(value)} tooltipProps={null} />;
    };
}
